import {
  hasPermissions,
  getUserPermissions,
  getCurrentAccount,
} from './permissionsHelper';

import {
  ROLES,
  CONVERSATION_PERMISSIONS,
  CONTACT_PERMISSIONS,
  REPORTS_PERMISSIONS,
  PORTAL_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

export const routeIsAccessibleFor = (route, userPermissions = []) => {
  const { meta: { permissions: routePermissions = [] } = {} } = route;
  return hasPermissions(routePermissions, userPermissions);
};

export const defaultRedirectPage = (to, permissions) => {
  const { accountId } = to.params;

  const permissionRoutes = [
    {
      permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      path: 'dashboard',
    },
    { permissions: [CONTACT_PERMISSIONS], path: 'contacts' },
    { permissions: [REPORTS_PERMISSIONS], path: 'reports/overview' },
    { permissions: [PORTAL_PERMISSIONS], path: 'portals' },
  ];

  const route = permissionRoutes.find(({ permissions: routePermissions }) =>
    hasPermissions(routePermissions, permissions)
  );

  return `accounts/${accountId}/${route ? route.path : 'dashboard'}`;
};

const CONVERSATION_ONLY_ACCESS_ROLES = [
  'agent',
  'team_leader',
  'supervisor',
];

const DATA_ADMIN_PATHS = [
  '/dashboard',
  '/inbox',
  '/contacts',
  '/companies',
  '/reports',
  '/calls',
  '/notifications',
  '/search',
];

const LIMITED_SETTINGS_ACCESS_ROLES = [
  'admin',
  'data_admin',
];

const LIMITED_SETTINGS_PATHS = [
  '/settings/agents',
  '/settings/teams',
  '/settings/inboxes',
  '/settings/labels',
];

const isLimitedSettingsPath = path =>
  LIMITED_SETTINGS_PATHS.some(settingsPath =>
    path.includes(settingsPath)
  );

const clinicexpertRouteRedirect = (to, user) => {
  const accountId = Number(to.params.accountId);
  const currentAccount = getCurrentAccount(user, accountId);
  const accessRole = currentAccount?.access_role;
  const dashboardPath = `accounts/${accountId}/dashboard`;

  if (CONVERSATION_ONLY_ACCESS_ROLES.includes(accessRole)) {
    const agentStatusAllowed =
      ['team_leader', 'supervisor'].includes(accessRole) &&
      [
        'agent_status',
        'agent_conversations',
        'agent_conversation',
      ].includes(to.name);

    const allowed =
      agentStatusAllowed ||
      isAConversationRoute(to.name, true, true) ||
      isAInboxViewRoute(to.name, true) ||
      ['dashboard', 'notifications_index'].includes(
        to.name
      );

    return allowed ? null : dashboardPath;
  }

  if (accessRole === 'data_admin') {
    if (to.path.includes('/settings')) {
      return isLimitedSettingsPath(to.path)
        ? null
        : dashboardPath;
    }

    const allowed = DATA_ADMIN_PATHS.some(path =>
      to.path.includes(path)
    );

    return allowed ? null : dashboardPath;
  }

  if (
    accessRole === 'admin' &&
    to.path.includes('/settings')
  ) {
    return isLimitedSettingsPath(to.path)
      ? null
      : dashboardPath;
  }

  return null;
};

const validateActiveAccountRoutes = (to, user) => {
  // If the current account is active, then check for the route permissions
  const accountDashboardURL = `accounts/${to.params.accountId}/dashboard`;

  const clinicexpertRedirect =
    clinicexpertRouteRedirect(to, user);

  if (clinicexpertRedirect) {
    return clinicexpertRedirect;
  }

  const currentAccount = getCurrentAccount(
    user,
    Number(to.params.accountId)
  );

  if (
    LIMITED_SETTINGS_ACCESS_ROLES.includes(
      currentAccount?.access_role
    ) &&
    isLimitedSettingsPath(to.path)
  ) {
    return null;
  }

  // If the user is trying to access suspended route, redirect them to dashboard
  if (to.name === 'account_suspended') {
    return accountDashboardURL;
  }

  const userPermissions = getUserPermissions(user, to.params.accountId);

  const isAccessible = routeIsAccessibleFor(to, userPermissions);
  // If the route is not accessible for the user, return to dashboard screen
  return isAccessible ? null : defaultRedirectPage(to, userPermissions);
};

export const validateLoggedInRoutes = (to, user) => {
  const currentAccount = getCurrentAccount(user, Number(to.params.accountId));
  // If current account is missing, either user does not have
  // access to the account or the account is deleted, return to login screen
  if (!currentAccount) {
    return `app/login`;
  }

  const isCurrentAccountActive = currentAccount.status === 'active';

  if (isCurrentAccountActive) {
    return validateActiveAccountRoutes(to, user);
  }

  // If the current account is not active, only the suspended screen is
  // reachable; administrators can also access billing to restore the account
  const userPermissions = getUserPermissions(user, to.params.accountId);
  const accessibleRoutes = userPermissions.includes('administrator')
    ? ['account_suspended', 'billing_settings_index']
    : ['account_suspended'];

  if (!accessibleRoutes.includes(to.name)) {
    return `accounts/${to.params.accountId}/suspended`;
  }

  // Proceed to the route if none of the above conditions are met
  return null;
};

export const isAConversationRoute = (
  routeName,
  includeBase = false,
  includeExtended = true
) => {
  const baseRoutes = [
    'home',
    'conversation_mentions',
    'conversation_unattended',
    'inbox_dashboard',
    'label_conversations',
    'team_conversations',
    'folder_conversations',
    'conversation_participating',
  ];
  const extendedRoutes = [
    'inbox_conversation',
    'conversation_through_mentions',
    'conversation_through_unattended',
    'conversation_through_inbox',
    'conversations_through_label',
    'conversations_through_team',
    'conversations_through_folders',
    'conversation_through_participating',
  ];

  const routes = [
    ...(includeBase ? baseRoutes : []),
    ...(includeExtended ? extendedRoutes : []),
  ];

  return routes.includes(routeName);
};

export const getConversationDashboardRoute = routeName => {
  switch (routeName) {
    case 'inbox_conversation':
      return 'home';
    case 'conversation_through_mentions':
      return 'conversation_mentions';
    case 'conversation_through_unattended':
      return 'conversation_unattended';
    case 'conversations_through_label':
      return 'label_conversations';
    case 'conversations_through_team':
      return 'team_conversations';
    case 'conversations_through_folders':
      return 'folder_conversations';
    case 'conversation_through_participating':
      return 'conversation_participating';
    case 'conversation_through_inbox':
      return 'inbox_dashboard';
    default:
      return null;
  }
};

export const isAInboxViewRoute = (routeName, includeBase = false) => {
  const baseRoutes = ['inbox_view'];
  const extendedRoutes = ['inbox_view_conversation'];
  const routeNames = includeBase
    ? [...baseRoutes, ...extendedRoutes]
    : extendedRoutes;
  return routeNames.includes(routeName);
};

export const isNotificationRoute = routeName =>
  routeName === 'notifications_index';
